import {
  SHIPMENT_HEALTH_LABELS,
  SHIPMENT_STAGE_LABELS,
  type ShipmentHealth,
  type ShipmentStage,
} from "@estelle/domain";
import {
  CircleCheck,
  Clock,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { reportingNow } from "@/lib/reportClock";
import {
  STAGE_TONE,
  exactIndiaTime,
  timeAgo,
  type StageTone,
} from "@/modules/orders/tracking";

/**
 * The stage badge and the health label (#527, Part B): the two small things
 * every tracking surface draws — the lists, the quick view, the order page.
 *
 * Both say their word in text and carry a dot or an icon besides, so colour is
 * never the only signal. Both are built on the app's own `Badge` and the colour
 * tokens the order page already uses for its statuses, each with a dark-theme
 * pair, so neither needs a primitive of its own.
 */

const TONE_CLASS: Record<StageTone, string> = {
  waiting: "border-border bg-muted text-foreground",
  moving: "border-transparent bg-tint-blue text-tint-blue-foreground",
  done: "border-transparent bg-tint-green text-tint-green-foreground",
  failed: "border-transparent bg-tint-yellow text-tint-yellow-foreground",
  returning: "border-transparent bg-tint-orange text-tint-orange-foreground",
  closed: "border-border bg-muted text-muted-foreground",
  lost: "border-destructive/40 bg-destructive/10 text-destructive",
};

/** Where a parcel is, in plain words, with a dot of its tone. */
export function StageBadge({
  stage,
  className,
}: {
  stage: ShipmentStage;
  className?: string;
}) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1.5 font-medium",
        TONE_CLASS[STAGE_TONE[stage]],
        className,
      )}
    >
      <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />
      {SHIPMENT_STAGE_LABELS[stage]}
    </Badge>
  );
}

const HEALTH_STYLE: Record<
  ShipmentHealth,
  { icon: LucideIcon; className: string }
> = {
  on_track: {
    icon: CircleCheck,
    className: "border-transparent bg-tint-green text-tint-green-foreground",
  },
  no_update: {
    icon: Clock,
    className: "border-transparent bg-tint-yellow text-tint-yellow-foreground",
  },
  delayed: {
    icon: TriangleAlert,
    className: "border-destructive/40 bg-destructive/10 text-destructive",
  },
};

/** Whether a parcel needs a look: On track, No update or Delayed. Nothing for a finished one. */
export function HealthLabel({
  health,
  className,
}: {
  health: ShipmentHealth | null;
  className?: string;
}) {
  if (health === null) return null;
  const { icon: Icon, className: tone } = HEALTH_STYLE[health];
  return (
    <Badge
      variant="outline"
      className={cn("gap-1.5 font-medium", tone, className)}
    >
      <Icon aria-hidden />
      {SHIPMENT_HEALTH_LABELS[health]}
    </Badge>
  );
}

/**
 * "3 hours ago", and the exact India time when asked — by hovering, which shows
 * the browser's own tip, or by tapping, which opens a small popover, since a
 * finger has no hover.
 *
 * The clock is `reportingNow()`, so a screen on sample data reads against the
 * fixture's own "now" and not the day somebody opens it.
 */
export function ExactTime({
  value,
  className,
}: {
  value: string;
  className?: string;
}) {
  const exact = exactIndiaTime(value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={exact}
          aria-label={`${timeAgo(value, reportingNow())}, ${exact}. Show the exact time.`}
          // The text is the target on a desktop. Under a finger it is padded to
          // a hand's size and pulled back by the same amount, so the line it
          // sits in does not grow.
          className={cn(
            "text-foreground cursor-pointer underline decoration-dotted underline-offset-4 pointer-coarse:-my-3.5 pointer-coarse:py-3.5",
            className,
          )}
        >
          {timeAgo(value, reportingNow())}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto max-w-[calc(100vw-2rem)] px-3 py-2 text-sm"
        align="start"
      >
        {exact}
      </PopoverContent>
    </Popover>
  );
}
